import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useEffect, useState } from 'react';
import { Checkbox } from '../../../components/ui/checkbox';

const graphOptionsInitial = [
  {
    key: 'user',
    title: 'You',
    color: 'var(--q3-orange-normal)',
    checked: true,
  },
  // {
  //   key: 'topper',
  //   title: 'Topper',
  //   color: 'var(--q3-green-normal)',
  //   checked: true,
  // },
  {
    key: 'top_10_percent',
    title: 'Top 10%ile',
    color: 'var(--q3-blue-normal)',
    checked: true,
  },
  {
    key: 'top_25_percent',
    title: 'Top 25%ile',
    color: 'var(--q3-yellow-normal)',
    checked: true,
  },
];

export default function ComparativeHourwiseChart({ comparativeData, selectedHourWiseTab }) {
  const [chartData, setChartData] = useState([]);
  const [graphOptions, setGraphOptions] = useState(graphOptionsInitial);

  useEffect(() => {
    if (
      !comparativeData ||
      !selectedHourWiseTab ||
      !comparativeData.userData?.hour_wise_breakdown ||
      !comparativeData.comparativeData?.hour_wise_breakdown
    )
      return;

    const userBreakdown = comparativeData.userData.hour_wise_breakdown;
    const compBreakdown = comparativeData.comparativeData.hour_wise_breakdown;
    const selectedKey = selectedHourWiseTab?.title?.toLowerCase();

    const data = userBreakdown.map((userItem, idx) => {
      const label = userItem.title;
      const compItem = compBreakdown[idx];

      return {
        name: label,
        user: selectedKey === 'overall' ? (userItem.values?.total ?? 0) : (userItem.values?.[selectedKey] ?? 0),
        // topper:
        //   selectedKey === 'overall'
        //     ? (compItem?.values?.topper?.total ?? 0)
        //     : (compItem?.values?.topper?.[selectedKey] ?? 0),
        top_10_percent:
          selectedKey === 'overall'
            ? (compItem?.values?.top_10_percent?.total ?? 0)
            : (compItem?.values?.top_10_percent?.[selectedKey] ?? 0),
        top_25_percent:
          selectedKey === 'overall'
            ? (compItem?.values?.top_25_percent?.total ?? 0)
            : (compItem?.values?.top_25_percent?.[selectedKey] ?? 0),
      };
    });

    setChartData(data);
  }, [comparativeData, selectedHourWiseTab]);

  const handleLineToggle = (value, index) => {
    const updated = graphOptions.map((opt, i) => ({
      ...opt,
      checked: i === index ? !opt.checked : opt.checked,
    }));
    setGraphOptions(updated);
  };

  const visibleLines = graphOptions.filter((line) => line.checked);

  if (!chartData || !chartData.length) return null;

  const maxY = Math.max(...chartData.flatMap((d) => visibleLines.map((line) => d[line.key] || 0)));
  const adjustedMaxY = Math.ceil(maxY * 1.1);

  return (
    <div className="border border-[var(--q3-stroke-light)] border-t-0 rounded-b-xl">
      <style jsx>{`
        .checkbox-custom[data-state='checked'] {
          background-color: var(--checkbox-bg) !important;
          border-color: var(--checkbox-border) !important;
        }
        .checkbox-custom:hover {
          border-color: var(--checkbox-border) !important;
        }
        .checkbox-custom:focus-visible {
          ring-color: var(--checkbox-border) !important;
        }
      `}</style>

      <div className="flex justify-start gap-4 flex-wrap mb-4 px-4 pt-2">
        {graphOptions.map((item, index) => (
          <div
            key={item.key}
            className="flex items-center cursor-pointer gap-2 border-b-2"
            style={{ borderBottomColor: item.checked ? item.color : 'transparent' }}
          >
            <Checkbox
              id={item.key}
              checked={item.checked}
              onCheckedChange={(value) => handleLineToggle(value, index)}
              className="checkbox-custom cursor-pointer rounded-[4px] border-[var(--q3-stroke-normal)]"
              style={{
                '--checkbox-bg': item.color,
                '--checkbox-border': item.color,
              }}
            />
            <label htmlFor={item.key} className="text-[var(--q3-neutral-default)] cursor-pointer text-sm">
              {item.title}
            </label>
          </div>
        ))}
      </div>

      <div className="w-full overflow-x-auto">
        <div className="min-w-[800px] h-[400px] pointer-events-none">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--q3-stroke-light)" />
              <XAxis
                dataKey="name"
                tickFormatter={(value) => value.replace('-', ' - ')}
                tick={{ fontSize: 12, fill: 'var(--q3-neutral-light)' }}
                stroke="var(--q3-stroke-light)"
              />
              <YAxis
                domain={[0, adjustedMaxY]}
                tickFormatter={(value) => `${value} Qs`}
                width={50}
                tick={{ fontSize: 12, fill: 'var(--q3-neutral-light)' }}
                stroke="var(--q3-stroke-light)"
              />
              <Tooltip
                formatter={(value, key) => {
                  const item = graphOptions.find((opt) => opt.key === key);
                  return [`${value} Qs`, item?.title ?? key];
                }}
                labelStyle={{ color: 'var(--q3-neutral-default)', fontSize: 12 }}
                contentStyle={{
                  borderRadius: 8,
                  border: '1px solid var(--q3-stroke-light)',
                  backgroundColor: 'var(--q3-surface-default)',
                  fontSize: 12,
                }}
              />
              {visibleLines.map((line) => (
                <Line
                  key={line.key}
                  type="monotone"
                  dataKey={line.key}
                  stroke={line.color}
                  strokeWidth={3}
                  dot={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
